// The chrome a settle / add-charge sheet lives inside (gdn-2957).
//
// Two variants, because the same sheet body now has two homes:
//
//  'drawer' — the original. The sheet is absolutely positioned against the
//    drawer body and slides up from its bottom edge. Kept for the ungated
//    path while PatientLedgerDrawer still exists.
//
//  'dialog' — the app's ordinary modal convention. GlobalModalManager's
//    Dialog already supplies the Paper, the backdrop and the slide-up
//    transition (SlideUpTransition), so this variant supplies none of them:
//    doubling up on either gives you a sheet that fights its own container.
//
// Bottom-edge anchoring was inherited from the drawer, where a sheet rising
// inside a side pane made sense. Nothing else in the app anchors a surface
// to the viewport edge on desktop, so the dialog variant is the one new
// callers should use.

import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Slide } from '@mui/material';

export type SheetSurfaceVariant = 'drawer' | 'dialog';

interface Props {
  variant?: SheetSurfaceVariant;
  /** Drawer variant only — share of the drawer body the sheet may occupy. */
  drawerMaxHeight?: string;
  children: React.ReactNode;
}

export const SheetSurface = ({
  variant = 'drawer',
  drawerMaxHeight = '85%',
  children,
}: Props) => {
  if (variant === 'dialog') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          // Leave the Dialog's own margins visible rather than filling the
          // viewport; the body scrolls inside this column.
          maxHeight: '85vh',
          bgcolor: ELIG_TOKENS.card,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          top: 'auto',
          bottom: 0,
          maxHeight: drawerMaxHeight,
          bgcolor: ELIG_TOKENS.card,
          borderTop: `1px solid ${ELIG_TOKENS.lineStrong}`,
          boxShadow: '0 -8px 24px rgba(28,30,133,0.10)',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        {children}
      </Box>
    </Slide>
  );
};
